import {
  useCallback,
  useEffect,
  useRef,
  MouseEventHandler,
  ReactNode,
} from "react";
import {
  Node,
  NodeProps,
  NodeResizer,
  ResizeDragEvent,
  ResizeParams,
} from "@xyflow/react";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useAtomCallback } from "jotai/utils";
import classNames from "classnames";
import {
  updateNodeSetter,
  TextNodeData,
  shouldShowStepIdsAtom,
  isViewOnlyModeAtom,
  saveNodeOnResizeEndSetter,
  canvasIdAtom,
  createNotificationSetter,
  saveCanvasSetter,
  isOnlyNodeSelectedAtomFamily,
  isEditingTextNodeAtomFamily,
  isMultiSelectedAtomFamily,
  isPublicAtom,
  togglesAtomResettable,
} from "@zapier/canvas-state";
import {
  ColorOrTransparent,
  RichTextEditor,
  RichTextEditorHandle,
} from "@zapier/rich-text-editor";
import type { RemirrorJSON } from "@zapier/rich-text-editor";

import { StepId } from "../../../StepId";
import { getNodeResizerHandleStyle, getNodeResizerLineStyle } from "../utils";
import styles from "./TextNode.module.css";

const DEFAULT_WIDTH = 290; // This is .rich-text-editor min-width + 40px padding (padding is specified in .editModeToggler x 2)
const DEFAULT_HEIGHT = 90; // This is .rich-text-editor min-height + 40px padding (padding is specified in .editModeToggler x 2)

// Component to handle toggling edit mode on click and wrap children
function EditModeToggler({
  onClick,
  children,
  isEditable,
  isMultiSelected,
}: {
  onClick: MouseEventHandler<HTMLDivElement>;
  children: ReactNode;
  isEditable: boolean;
  isMultiSelected: boolean;
}) {
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      onClick={onClick}
      className={classNames(styles.editModeToggler, {
        [styles.editable]: isEditable,
        [styles.wrapperMultiSelected]: isMultiSelected,
        /* IMPORTANT: We need the "nodrag" class or else you can't highlight / select text in edit mode */
        nodrag: isEditable,
      })}
    >
      {children}
    </div>
  );
}

export function TextNode(props: NodeProps<Node<TextNodeData>>) {
  const {
    id,
    dragging,
    data: { label, backgroundColor },
  } = props;

  const isViewOnlyMode = useAtomValue(isViewOnlyModeAtom);
  const isSelected = useAtomValue(isOnlyNodeSelectedAtomFamily(id));
  const isMultiSelected = useAtomValue(isMultiSelectedAtomFamily(id));
  const [isEditingText, setIsEditingText] = useAtom(
    isEditingTextNodeAtomFamily(id),
  );
  const updateNode = useSetAtom(updateNodeSetter);
  const shouldShowStepIds = useAtomValue(shouldShowStepIdsAtom);
  const saveToBackend = useSetAtom(saveCanvasSetter);
  const saveOnResizeEnd = useSetAtom(saveNodeOnResizeEndSetter);
  const canvasId = useAtomValue(canvasIdAtom);
  const isPublic = useAtomValue(isPublicAtom);
  const { blocked_domains } = useAtomValue(togglesAtomResettable);
  const editorRef = useRef<RichTextEditorHandle>(null);

  const isNodeSelected = !isViewOnlyMode && !dragging && isSelected;
  // Dragging is only true if the node is the only one being dragged (false when multiple nodes are being dragged)
  const isNodeSelectedOrDragged = !isViewOnlyMode && (dragging || isSelected);
  const isActive = isNodeSelected && !isEditingText;

  const isEditable =
    !isViewOnlyMode && !dragging && isSelected && isEditingText;

  /* When entering active mode, select all content in Remirror, so the WysiwygToolbar buttons
  will work properly and apply to all content */
  useEffect(() => {
    if (
      isActive &&
      editorRef.current &&
      typeof editorRef.current.selectAll === "function"
    ) {
      editorRef.current.selectAll();
    }
  }, [isActive]);

  /* When entering editable mode, focus the editor to show the text cursor flashing
  so user can start typing right away
   */
  useEffect(() => {
    if (isEditable && editorRef.current) {
      editorRef.current.focus();
    }
  }, [isEditable]);

  useEffect(() => {
    if (!isNodeSelected) {
      setIsEditingText(false); // Exit editing mode on deselect
    }
  }, [isNodeSelected, setIsEditingText]);

  const handleResizeEnd = useCallback(
    (_e: ResizeDragEvent, { width, height, x, y }: ResizeParams) => {
      saveOnResizeEnd({ id, width, height, x, y });
    },
    [saveOnResizeEnd, id],
  );

  const onBlur = useCallback(
    async (json: RemirrorJSON) => {
      const updatedNodes = updateNode({ id, data: { label: json } });
      saveToBackend({ nodes: updatedNodes });
    },
    [id, saveToBackend, updateNode],
  );

  const onBackgroundColorChange = useCallback(
    async (backgroundColor: ColorOrTransparent) => {
      const updatedNodes = updateNode({
        id,
        style: {
          backgroundColor,
        },
        data: {
          backgroundColor,
        },
      });

      saveToBackend({ nodes: updatedNodes });
    },
    [id, saveToBackend, updateNode],
  );

  const handleImageUploadError = useAtomCallback(
    useCallback((_get, set, message?: string) => {
      set(createNotificationSetter, {
        type: "error",
        message:
          message ||
          "We ran into an issue uploading your image. Please try again.",
      });
    }, []),
  );

  // Click handler to switch between modes
  const handleNodeClick = () => {
    if (isSelected && !isEditingText) {
      setIsEditingText(true); // Enter text editing mode
    }
    // else: already editing, do nothing
  };

  return (
    <>
      {shouldShowStepIds && <StepId />}
      <NodeResizer
        minWidth={DEFAULT_WIDTH}
        minHeight={DEFAULT_HEIGHT}
        handleStyle={getNodeResizerHandleStyle(isEditable)}
        lineStyle={getNodeResizerLineStyle(isEditable)}
        isVisible={isNodeSelectedOrDragged}
        onResizeEnd={handleResizeEnd}
      />
      <EditModeToggler
        onClick={handleNodeClick}
        isEditable={isEditable}
        isMultiSelected={isMultiSelected}
      >
        <RichTextEditor
          canvasId={canvasId}
          nodeId={id}
          ref={editorRef}
          placeholder="Add text"
          initialContent={label}
          onBlur={onBlur}
          onBackgroundColorChange={onBackgroundColorChange}
          backgroundColor={
            (backgroundColor ?? "transparent") as ColorOrTransparent
          }
          editable={isEditable}
          isToolbarVisible={isNodeSelected}
          onImageUploadError={handleImageUploadError}
          isPublic={isPublic}
          blockedDomains={blocked_domains.config.domains}
        />
      </EditModeToggler>
    </>
  );
}
